/** Anything Firestore-ish that can become a Date. */
export type DateLike = Date | number | { toDate: () => Date } | null | undefined;

export function toDate(value: DateLike): Date | null {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'object' && 'toDate' in value) return value.toDate();
  return null;
}

export function toMillis(value: DateLike): number {
  return toDate(value)?.getTime() ?? 0;
}

export function formatDate(value: DateLike, locale?: string): string {
  const d = toDate(value);
  return d ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(d) : '—';
}

export function formatDateTime(value: DateLike, locale?: string): string {
  const d = toDate(value);
  return d
    ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(d)
    : '—';
}

export function formatTime(value: DateLike, locale?: string): string {
  const d = toDate(value);
  return d ? new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(d) : '';
}

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 31_536_000],
  ['month', 2_592_000],
  ['week', 604_800],
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
];

export function formatRelative(value: DateLike, now = Date.now(), locale?: string): string {
  const d = toDate(value);
  if (!d) return '';
  const seconds = Math.round((d.getTime() - now) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  for (const [unit, size] of UNITS) {
    if (Math.abs(seconds) >= size) return rtf.format(Math.round(seconds / size), unit);
  }
  return rtf.format(0, 'minute');
}

/** "from 3 Jan 2026 to Present" / "from … to …" */
export function stayPeriod(start: DateLike, end: DateLike, locale?: string): string {
  return `from ${formatDate(start, locale)} to ${end ? formatDate(end, locale) : 'Present'}`;
}
